/**
 * Indicação de afiliado no cadastro (hub#739).
 *
 * A pessoa chega pela página pública (`imobgo.app/public/a/{code}`), que grava o cookie
 * `imobgo_ref` no domínio compartilhado e manda para cá com `?ref=` na URL. Quem fecha o
 * ciclo é este módulo: sem ler nada disso, todo o rastro de telemetria e todo o link
 * divulgado morrem no último passo, e o afiliado nunca recebe pelo cliente que trouxe.
 *
 * Duas fontes de propósito. A URL cobre quem clica e se cadastra na hora; o cookie cobre
 * quem fecha a aba e volta pelo site dias depois — que é o caso mais comum.
 */

/** Nome do cookie gravado pela página pública. Precisa bater com `REF_COOKIE` de lá. */
const REF_COOKIE = "imobgo_ref";

/** Token de convite particular: uso único, então vive só enquanto a aba viver. */
const INVITE_STORAGE_KEY = "imobgo.affiliateInvite";

/** E-mail para quem o convite foi emitido — conveniência, não credencial. */
const INVITE_EMAIL_KEY = "imobgo.affiliateInviteEmail";

/**
 * Código público do afiliado: slug minúsculo gerado do nome (`marina-alves-k7m2`).
 * Sem prefixo `AF-` — supor caixa alta aqui descartaria todos os códigos reais.
 */
const CODE_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

// Barreira de formato, não validação de e-mail: o objetivo é impedir que lixo da URL vire
// o valor inicial de um campo. Quem valida de verdade é o schema do formulário.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface AffiliateReferral {
  code?: string;
  inviteToken?: string;
}

function normalizeCode(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  return CODE_PATTERN.test(normalized) ? normalized : undefined;
}

function normalizeEmail(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  return EMAIL_PATTERN.test(normalized) ? normalized : undefined;
}

function normalizeToken(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  // Token é opaco e sensível ao caixa — normalizar aqui o invalidaria do outro lado.
  return TOKEN_PATTERN.test(trimmed) ? trimmed : undefined;
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const found = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!found) return undefined;

  try {
    return decodeURIComponent(found.slice(prefix.length));
  } catch {
    // Cookie corrompido não pode derrubar o cadastro — só deixa de indicar.
    return undefined;
  }
}

function writeRefCookie(code: string): void {
  const days = 30;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();

  // Sem `Domain`: aqui já estamos no host do portal. A página pública grava a versão com
  // `Domain=.imobgo.app`; esta só reforça o que veio pela URL, para sobreviver ao reload.
  document.cookie = `${REF_COOKIE}=${encodeURIComponent(code)}; Path=/; Expires=${expires}; SameSite=Lax`;
}

/**
 * Lê `?ref=` e `?affiliate_invite=` da URL e os persiste.
 *
 * Chamado uma vez no boot. É o que faz a indicação sobreviver ao caminho real do usuário:
 * ele chega pelo link, navega por três telas do wizard e recarrega a página no meio.
 */
export function captureAffiliateReferralFromUrl(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);

  const code = normalizeCode(params.get("ref"));
  if (code) writeRefCookie(code);

  const inviteToken = normalizeToken(params.get("affiliate_invite"));
  if (inviteToken) {
    try {
      sessionStorage.setItem(INVITE_STORAGE_KEY, inviteToken);

      // O e-mail só é guardado JUNTO com um convite válido. Aceitar `?email=` solto faria
      // qualquer link preencher o cadastro de outra pessoa — que é como um phishing
      // começa, e aqui não compraria nada: sem convite não há indicação a preservar.
      const invitedEmail = normalizeEmail(params.get("email"));
      if (invitedEmail) sessionStorage.setItem(INVITE_EMAIL_KEY, invitedEmail);
    } catch {
      // Modo privado sem storage: segue sem o convite, e o cadastro acontece igual.
    }
  }
}

/**
 * E-mail para quem o convite foi emitido, se houver.
 *
 * A página do convite já mandava `&email=` na URL dizendo que pré-preenchia o cadastro, e
 * **nada lia esse parâmetro** — o campo abria vazio e a pessoa redigitava o endereço em
 * que acabou de receber o convite.
 *
 * <p>
 * Preenche, mas **não trava**. Travar seria o desenho do convite de conta (hub#489), onde
 * o e-mail É a identidade convidada; aqui ele é conveniência — quem foi convidado num
 * endereço e prefere se cadastrar em outro não pode ser impedido, porque o que carrega a
 * indicação é o token, não o e-mail.
 * </p>
 */
export function readInvitedEmail(): string | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    return normalizeEmail(sessionStorage.getItem(INVITE_EMAIL_KEY));
  } catch {
    return undefined;
  }
}

/**
 * O que enviar ao backend no commit do cadastro.
 *
 * A URL vence o cookie: se a pessoa acabou de clicar no link de outro afiliado, é essa a
 * indicação viva. Quem decide o first-touch é o affiliates-api, com a atribuição do
 * tenant — aqui não cabe adivinhar.
 */
export function readAffiliateReferral(): AffiliateReferral {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const code = normalizeCode(params.get("ref")) ?? normalizeCode(readCookie(REF_COOKIE));

  let inviteToken = normalizeToken(params.get("affiliate_invite"));
  if (!inviteToken) {
    try {
      inviteToken = normalizeToken(sessionStorage.getItem(INVITE_STORAGE_KEY));
    } catch {
      inviteToken = undefined;
    }
  }

  return { code, inviteToken };
}

/** Identificação visível da indicação que vai ser creditada. */
export interface ReferralBadge {
  /** `invite` = convite pessoal (token); `code` = link do afiliado. */
  kind: "invite" | "code";
  /** O que aparece na tela e serve para o suporte casar o cadastro com a indicação. */
  id: string;
}

/**
 * Prefixo do token do convite. O token inteiro não vai à tela: ele é o que credita a
 * indicação, e a pessoa costuma fotografar ou compartilhar a tela de cadastro. Oito
 * caracteres bastam para o suporte localizar o convite e não servem para reusá-lo.
 */
const BADGE_TOKEN_LENGTH = 8;

/**
 * Identifica na tela de cadastro qual indicação está viva.
 *
 * Existe porque a indicação é invisível: ela viaja em cookie e sessionStorage e só é
 * enviada três telas adiante, no commit. Sem nada na tela, nem o indicado nem o afiliado
 * têm como saber se o vínculo sobreviveu ao caminho — e o afiliado só descobriria que
 * não sobreviveu quando a comissão não aparecesse.
 *
 * O convite vence o código: quando os dois existem, é o convite que credita.
 */
export function readReferralBadge(): ReferralBadge | undefined {
  const { code, inviteToken } = readAffiliateReferral();

  if (inviteToken) {
    return { kind: "invite", id: inviteToken.slice(0, BADGE_TOKEN_LENGTH) };
  }
  return code ? { kind: "code", id: code } : undefined;
}

/** Limpa após o cadastro concluir — o convite é de uso único e não deve vazar para o próximo. */
export function clearAffiliateReferral(): void {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.removeItem(INVITE_STORAGE_KEY);
    sessionStorage.removeItem(INVITE_EMAIL_KEY);
  } catch {
    // Sem storage não há o que limpar.
  }

  document.cookie = `${REF_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}
